import React from "react";
import { cn, getSolidBackgroundStyles, getFocusRingStyles } from "../../styles";
import { ThemeColor } from "../../props";

export interface SwitchProps {
  /**
   * Whether the switch is in the checked (on) state.
   * @default false
   */
  checked?: boolean;

  /**
   * Callback fired when the switch state changes.
   * Receives the new checked state as a boolean parameter.
   */
  onChange?: (checked: boolean) => void;

  /**
   * If true, the switch will be disabled and non-interactive.
   * @default false
   */
  disabled?: boolean;

  /**
   * Additional class name(s) to apply to the switch element.
   */
  className?: string;

  /**
   * HTML id attribute for the switch element.
   */
  id?: string;

  /**
   * Color used for the switch background when checked.
   * @default ThemeColor.primary
   */
  color?: ThemeColor;

  /**
   * Color used for the focus ring. If not provided, uses `color` prop.
   */
  focusColor?: ThemeColor;

  /**
   * Color used for the focus ring when unchecked.
   * If null, falls back to `focusColor` or `color`.
   * @default ThemeColor.primary
   */
  focusColorUnchecked?: ThemeColor;

  /**
   * Color used for the switch background when unchecked (off state).
   * When not provided, defaults to a neutral gray.
   */
  offColor?: ThemeColor;

  /**
   * If true, renders a smaller compact version of the switch.
   * @default false
   */
  dense?: boolean;

  /**
   * Accessible label for the switch.
   */
  "aria-label"?: string;

  /**
   * ID of element that describes the switch.
   */
  "aria-describedby"?: string;
}

/**
 * A toggle switch component that provides an on/off binary choice.
 * Supports keyboard navigation, accessibility features, and customizable colors.
 * Available in standard and dense (compact) sizes.
 */
export const Switch: React.FC<SwitchProps> = ({
  checked = false,
  onChange,
  disabled = false,
  className,
  id,
  color = ThemeColor.primary,
  offColor,
  focusColor,
  focusColorUnchecked = ThemeColor.primary,
  dense = false,
  ...ariaProps
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e.target.checked);
    }
  };

  // Determine focus color based on checked state
  const currentFocusColor = checked
    ? focusColor || color
    : focusColorUnchecked || focusColor || color;
  const focusRingColor = getFocusRingStyles(currentFocusColor);

  // Size variants for dense mode
  const containerSize = dense ? "w-8 h-4" : "w-10 h-5";
  const toggleSize = dense ? "before:w-3 before:h-3" : "before:w-4 before:h-4";
  const togglePosition = dense
    ? checked
      ? "before:translate-x-4"
      : "before:translate-x-0.5"
    : checked
      ? "before:translate-x-5"
      : "before:translate-x-1";
  const toggleVerticalOffset = dense
    ? "before:translate-y-0.5"
    : "before:translate-y-0.5";

  return (
    <input
      type="checkbox"
      role="switch"
      checked={checked}
      onChange={handleChange}
      disabled={disabled}
      id={id}
      className={cn(
        "relative rounded-full transition-all duration-200 appearance-none cursor-pointer",
        "shrink-0",
        containerSize,
        "focus:outline-none focus:ring-2 focus:ring-offset-2",
        focusRingColor,
        "before:content-[''] before:absolute before:bg-back-50 before:dark:bg-back-50",
        toggleSize,
        "before:rounded-full before:shadow-md before:transition-transform before:duration-200",
        toggleVerticalOffset,
        togglePosition,
        checked
          ? getSolidBackgroundStyles(color)
          : offColor
            ? getSolidBackgroundStyles(offColor)
            : "bg-back-300 dark:bg-back-700",
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
      {...ariaProps}
    />
  );
};
